import { useEffect, useMemo, useState } from "react";
import { ReceiptText } from "lucide-react";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { cn } from "../../lib/utils.js";
import { COMP_LABELS, humanize, takeHomeBeforeTax } from "../../lib/compensation.js";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog.js";
import { Skeleton } from "../ui/skeleton.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table.js";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs.js";
import { ErrorBanner } from "../reports/ReportKit.js";
import { BASIS_OPTIONS, fmtAsOf, type PayBasis, type PayToDate } from "./payBasis.js";

interface BreakupLine { key: string; monthly: number; payable: number | null; toDate: number | null }
interface Breakup {
  employee: { id: string; name: string; code: string; department: string | null; office: string | null };
  month: string;
  asOf: string | null;
  workingDays: number;
  payableDays: number;
  lopDays: number;
  toDate: PayToDate;
  joinDate: string | null;
  exitDate: string | null;
  structure: { ctcAnnual: number; effectiveFrom: string; reason: string } | null;
  earnings: BreakupLine[];
  deductions: BreakupLine[];
  employer: BreakupLine[];
  bonusAnnual: number | null;
  totals: { grossMonthly: number; grossPayable: number; grossToDate: number; perDay: number; deductionsMonthly: number };
}

const MONEY = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });
const money = (n: number) => MONEY.format(n);
const MONTH = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
const label = (key: string) => COMP_LABELS[key] ?? humanize(key);

const NUM = "px-3 text-right tabular-nums whitespace-nowrap";

/**
 * A section of the breakup: a heading row, then one row per component. `split` adds the
 * second column (the amount for the payable days, or for the days earned so far) -- in
 * month view only when fewer days are payable; otherwise the two would be the same figure.
 */
function Section({ title, lines, prorated, split, basis }: { title: string; lines: BreakupLine[]; prorated: boolean; split: boolean; basis: PayBasis }) {
  if (lines.length === 0) return null;
  return (
    <>
      <TableRow className="bg-surface-50 hover:bg-surface-50">
        <TableCell colSpan={split ? 3 : 2} className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.5px] text-surface-600">{title}</TableCell>
      </TableRow>
      {lines.map((l) => {
        const amount = basis === "toDate" ? l.toDate : l.payable;
        return (
          <TableRow key={l.key}>
            <TableCell className="px-3 text-surface-800">{label(l.key)}</TableCell>
            <TableCell className={NUM}>{money(l.monthly)}</TableCell>
            {split && (
              <TableCell className={cn(NUM, prorated ? "font-semibold text-surface-900" : "text-surface-400")}>
                {amount === null ? "—" : money(amount)}
              </TableCell>
            )}
          </TableRow>
        );
      })}
    </>
  );
}

/**
 * One employee's salary breakup for the month, opened from the icon on a Pay Inputs row:
 * the approved structure's components, with earnings worked out for the month's payable
 * days or for the days earned so far (`basis`, switchable inside the dialog).
 */
export function SalaryBreakupDialog({ employeeId, month, basis, onClose }: { employeeId: string | null; month: string; basis: PayBasis; onClose: () => void }) {
  const [data, setData] = useState<Breakup | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Opens on the table's basis; switching here does not change the table.
  const [shown, setShown] = useState<PayBasis>(basis);
  useEffect(() => { if (employeeId) setShown(basis); }, [employeeId, basis]);

  useEffect(() => {
    if (!employeeId) return;
    let cancelled = false;
    setData(null);
    setError(null);
    api.get<Breakup>(`/reports/hr/payroll/breakup?month=${month}&employeeId=${encodeURIComponent(employeeId)}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => {
        if (cancelled) return;
        logError("SalaryBreakupDialog.load", err);
        setError(errMsg(err) ?? "Failed to load the salary breakup");
      });
    return () => { cancelled = true; };
  }, [employeeId, month]);

  // The days the second column pays: the month's payable days, or those earned so far.
  const days = !data ? 0 : shown === "toDate" ? data.toDate.payable : data.payableDays;
  // Month view: a second column only when some working days are not paid (joined / left
  // mid-month, LOP). Pay to date always has one -- it is the point of the view.
  const split = !!data && (shown === "toDate" || data.payableDays < data.workingDays);
  // In-hand (before tax) with the app's one formula (profile, revision compare), fed the
  // structure's monthly lines; for fewer payable days it scales like the earnings do.
  const inHand = useMemo(() => {
    if (!data) return { monthly: 0, payable: 0 };
    const lines = [...data.earnings, ...data.deductions, ...data.employer]
      .map((l) => ({ componentKey: l.key, annualAmount: l.monthly, monthlyAmount: l.monthly }));
    const monthly = Math.round(takeHomeBeforeTax(lines) * 100) / 100;
    const payable = data.workingDays > 0 ? Math.round((monthly * days / data.workingDays) * 100) / 100 : 0;
    return { monthly, payable };
  }, [data, days]);
  const monthLabel = MONTH.format(new Date(`${month}-01T00:00:00Z`));
  const asOf = data?.asOf ? fmtAsOf(data.asOf) : null;

  return (
    <Dialog open={!!employeeId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ReceiptText size={16} /> {data ? `${data.employee.name} · ${data.employee.code}` : "Salary breakup"}
          </DialogTitle>
          <DialogDescription>
            Salary breakup for {monthLabel}
            {data?.structure ? ` · CTC ${money(data.structure.ctcAnnual)} a year, effective ${data.structure.effectiveFrom}` : ""}
          </DialogDescription>
        </DialogHeader>
        <ErrorBanner message={error} />

        {!data && !error && (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        )}

        {data && (
          <div className="flex flex-col gap-3">
            <Tabs value={shown} onValueChange={(v) => setShown(v === "toDate" ? "toDate" : "month")}>
              <TabsList>
                {BASIS_OPTIONS.map((o) => <TabsTrigger key={o.value} value={o.value}>{o.label}</TabsTrigger>)}
              </TabsList>
            </Tabs>

            {shown === "month" ? (
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-surface-700">
                <span>Working days <b className="text-surface-900">{data.workingDays}</b></span>
                <span>Payable days <b className="text-surface-900">{data.payableDays}</b></span>
                <span>LOP <b className={cn(data.lopDays > 0 ? "text-[var(--color-warning-fg)]" : "text-surface-900")}>{data.lopDays}</b></span>
                <span>Per day <b className="text-surface-900">{money(data.totals.perDay)}</b></span>
                {data.joinDate && <span>Joined <b className="text-surface-900">{data.joinDate}</b></span>}
                {data.exitDate && <span>Last working day <b className="text-surface-900">{data.exitDate}</b></span>}
              </div>
            ) : (
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-surface-700">
                <span>{asOf ? <>As of <b className="text-surface-900">{asOf}</b></> : `${monthLabel} has not started`}</span>
                <span>Earned <b className="text-surface-900">{data.toDate.payable}</b> of {data.workingDays} days</span>
                <span>Present <b className="text-surface-900">{data.toDate.present}</b></span>
                <span>Paid leave <b className="text-surface-900">{data.toDate.paidLeave}</b></span>
                <span>LOP <b className={cn(data.toDate.lop > 0 ? "text-[var(--color-warning-fg)]" : "text-surface-900")}>{data.toDate.lop}</b></span>
                <span>Absent <b className={cn(data.toDate.absent > 0 ? "text-[var(--color-error-fg)]" : "text-surface-900")}>{data.toDate.absent}</b></span>
                <span>Days left <b className="text-surface-900">{data.toDate.remaining}</b></span>
                {data.toDate.missedCheckouts > 0 && (
                  <span title="Checked in but never checked out — paid, worth a look before the run">
                    No check-out <b className="text-[var(--color-warning-fg)]">{data.toDate.missedCheckouts}</b>
                  </span>
                )}
              </div>
            )}

            {!data.structure ? (
              <p className="rounded-md bg-surface-50 px-3 py-4 text-center text-[13px] text-surface-500">
                No approved salary structure is in force for {monthLabel}.
              </p>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto rounded-md border border-surface-200">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-surface-50">
                      <TableHead className="px-3">Component</TableHead>
                      <TableHead className="px-3 text-right" title="The salary structure’s monthly amount, for all the month’s working days">
                        {split ? "Full month" : "Monthly"} ({data.workingDays} working days)
                      </TableHead>
                      {split && (
                        shown === "toDate" ? (
                          <TableHead className="px-3 text-right" title="Monthly ÷ working days × days earned so far">
                            Earned so far ({days} of {data.workingDays} working days)
                          </TableHead>
                        ) : (
                          <TableHead className="px-3 text-right" title="Monthly ÷ working days × payable days">
                            Payable ({days} of {data.workingDays} working days)
                          </TableHead>
                        )
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <Section title="Earnings" lines={data.earnings} prorated split={split} basis={shown} />
                    <TableRow className="bg-surface-50 font-bold hover:bg-surface-50">
                      <TableCell className="px-3 text-surface-900">Gross salary</TableCell>
                      <TableCell className={NUM}>{money(data.totals.grossMonthly)}</TableCell>
                      {split && (
                        <TableCell className={cn(NUM, "text-surface-900")}>
                          {money(shown === "toDate" ? data.totals.grossToDate : data.totals.grossPayable)}
                        </TableCell>
                      )}
                    </TableRow>
                    <Section title="Employee deductions (as per structure)" lines={data.deductions} prorated={false} split={split} basis={shown} />
                    <Section title="Employer contributions (as per structure)" lines={data.employer} prorated={false} split={split} basis={shown} />
                    <TableRow className="bg-[var(--color-success-bg)] font-bold hover:bg-[var(--color-success-bg)]">
                      <TableCell className="px-3 text-[var(--color-success-fg)]" title="Gross less gratuity, PF and ESI (employee and employer) and medical insurance — the same In-hand as the employee profile">
                        In-hand (before tax)
                      </TableCell>
                      <TableCell className={cn(NUM, "text-[var(--color-success-fg)]")}>{money(inHand.monthly)}</TableCell>
                      {split && <TableCell className={cn(NUM, "text-[var(--color-success-fg)]")}>{money(inHand.payable)}</TableCell>}
                    </TableRow>
                    {data.bonusAnnual !== null && (
                      <TableRow>
                        <TableCell className="px-3 text-surface-800">Bonus (paid yearly, not monthly)</TableCell>
                        <TableCell className={NUM}>{money(data.bonusAnnual)} / yr</TableCell>
                        {split && <TableCell className={cn(NUM, "text-surface-400")}>—</TableCell>}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* <p className="text-[11px] text-surface-500">
              Salary is for working days (weekdays less public holidays). When fewer days are payable, earnings and in-hand = monthly ÷ working days × payable days. Deductions and employer contributions are
              the structure&apos;s monthly figures; the payroll system works out the final statutory amounts.
            </p> */}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
